import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CartItem,
  ShippingAddress,
  BillingAddress,
  PaymentMethod,
} from '../../types/shop';

// Mock cart data - in a real app this would come from context/state management
const mockCartItems: CartItem[] = [
  {
    id: '1',
    itemId: '1',
    quantity: 2,
    item: {
      id: '1',
      name: 'PEO Foundation T-Shirt',
      description:
        'Comfortable cotton t-shirt featuring the PEO Foundation logo',
      shortDescription: 'PEO Foundation branded t-shirt',
      price: 25.0,
      category: 'Clothing',
      tags: ['clothing', 'foundation', 'branded'],
      images: [
        'https://via.placeholder.com/400x300/1976d2/ffffff?text=PEO+T-Shirt',
      ],
      thumbnail:
        'https://via.placeholder.com/400x300/1976d2/ffffff?text=PEO+T-Shirt',
      inStock: true,
      variants: [],
      featured: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-01'),
    },
    price: 25.0,
  },
];

const steps = ['Shipping Address', 'Billing Address', 'Payment', 'Review'];

export default function Checkout() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [shippingAddress, setShippingAddress] = useState<
    Partial<ShippingAddress>
  >({
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: '',
  });
  const [billingAddress, setBillingAddress] = useState<Partial<BillingAddress>>(
    {
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phone: '',
      sameAsShipping: true,
    }
  );
  const [paymentMethod, setPaymentMethod] = useState<Partial<PaymentMethod>>({
    type: 'card',
    last4: '',
    brand: '',
    expiryMonth: undefined,
    expiryYear: undefined,
  });

  const getSubtotal = () => {
    return mockCartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
  };

  const getTax = () => {
    return getSubtotal() * 0.08; // 8% tax
  };

  const getShipping = () => {
    return getSubtotal() > 50 ? 0 : 5.99; // Free shipping over $50
  };

  const getTotal = () => {
    return getSubtotal() + getTax() + getShipping();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleShippingChange = (
    field: keyof ShippingAddress,
    value: string
  ) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
    if (billingAddress.sameAsShipping) {
      setBillingAddress((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleBillingChange = (
    field: keyof BillingAddress,
    value: string | boolean
  ) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleSameAsShipping = (checked: boolean) => {
    setBillingAddress((prev) => ({
      ...prev,
      sameAsShipping: checked,
      ...(checked ? shippingAddress : {}),
    }));
  };

  const handleSubmit = () => {
    // In a real app, this would submit the order

    // Navigate to success page or show success message
    alert('Order submitted successfully!');
    navigate('/shop');
  };

  const renderShippingAddress = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Shipping Address
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={shippingAddress.firstName}
            onChange={(e) => handleShippingChange('firstName', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={shippingAddress.lastName}
            onChange={(e) => handleShippingChange('lastName', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company (Optional)"
            value={shippingAddress.company}
            onChange={(e) => handleShippingChange('company', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={shippingAddress.address1}
            onChange={(e) => handleShippingChange('address1', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            value={shippingAddress.address2}
            onChange={(e) => handleShippingChange('address2', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={shippingAddress.city}
            onChange={(e) => handleShippingChange('city', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="State"
            value={shippingAddress.state}
            onChange={(e) => handleShippingChange('state', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={shippingAddress.zipCode}
            onChange={(e) => handleShippingChange('zipCode', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Country"
            value={shippingAddress.country}
            onChange={(e) => handleShippingChange('country', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            value={shippingAddress.phone}
            onChange={(e) => handleShippingChange('phone', e.target.value)}
            required
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBillingAddress = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Billing Address
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={billingAddress.sameAsShipping}
            onChange={(e) => handleSameAsShipping(e.target.checked)}
          />
        }
        label="Same as shipping address"
        sx={{ mb: 2 }}
      />

      {!billingAddress.sameAsShipping && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={billingAddress.firstName}
              onChange={(e) => handleBillingChange('firstName', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={billingAddress.lastName}
              onChange={(e) => handleBillingChange('lastName', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company (Optional)"
              value={billingAddress.company}
              onChange={(e) => handleBillingChange('company', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={billingAddress.address1}
              onChange={(e) => handleBillingChange('address1', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2 (Optional)"
              value={billingAddress.address2}
              onChange={(e) => handleBillingChange('address2', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={billingAddress.city}
              onChange={(e) => handleBillingChange('city', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={billingAddress.state}
              onChange={(e) => handleBillingChange('state', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={billingAddress.zipCode}
              onChange={(e) => handleBillingChange('zipCode', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              value={billingAddress.country}
              onChange={(e) => handleBillingChange('country', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={billingAddress.phone}
              onChange={(e) => handleBillingChange('phone', e.target.value)}
              required
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderPayment = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Method
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Payment Type</InputLabel>
            <Select
              value={paymentMethod.type}
              label="Payment Type"
              onChange={(e) =>
                setPaymentMethod((prev) => ({
                  ...prev,
                  type: e.target.value as any,
                }))
              }
            >
              <MenuItem value="card">Credit/Debit Card</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {paymentMethod.type === 'card' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Card Number"
                placeholder="1234 5678 9012 3456"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Month"
                placeholder="MM"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Year"
                placeholder="YYYY"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="CVV" placeholder="123" required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name on Card" required />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Order Review
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle1" gutterBottom>
            Items ({mockCartItems.length})
          </Typography>
          {mockCartItems.map((item) => (
            <Box
              key={item.id}
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography>
                {item.item.name} x {item.quantity}
              </Typography>
              <Typography>
                ${(item.item.price * item.quantity).toFixed(2)}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            Shipping Address
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {shippingAddress.firstName} {shippingAddress.lastName}
            <br />
            {shippingAddress.address1}
            <br />
            {shippingAddress.address2 && `${shippingAddress.address2}<br />`}
            {shippingAddress.city}, {shippingAddress.state}{' '}
            {shippingAddress.zipCode}
            <br />
            {shippingAddress.country}
            <br />
            {shippingAddress.phone}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box sx={{ my: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography>Subtotal</Typography>
                  <Typography>${getSubtotal().toFixed(2)}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography>Tax</Typography>
                  <Typography>${getTax().toFixed(2)}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography>Shipping</Typography>
                  <Typography>
                    {getShipping() === 0
                      ? 'Free'
                      : `$${getShipping().toFixed(2)}`}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    ${getTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderShippingAddress();
      case 1:
        return renderBillingAddress();
      case 2:
        return renderPayment();
      case 3:
        return renderReview();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 'bold' }}
      >
        Checkout
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 4, mb: 2 }}>{getStepContent(activeStep)}</Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Back
        </Button>
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              size="large"
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              Place Order
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              size="large"
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
}
